/**
 * HIPAA Acknowledgment Modal
 * Required compliance acknowledgment before accessing PHI
 */
import React, { useState } from 'react';
import { Shield, CheckCircle, FileText, Lock, Eye, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

const HIPAAAcknowledgmentModal = ({ onAcknowledge, show }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!show) return null;

  const handleAcknowledge = async () => {
    if (!acknowledged) return;
    setLoading(true);
    await onAcknowledge();
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="hipaa-acknowledgment-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div className="text-white">
              <h2 className="text-xl font-bold">HIPAA Compliance Acknowledgment</h2>
              <p className="text-blue-100">Required before accessing Protected Health Information</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Health Insurance Portability and Accountability Act (HIPAA)
            </h3>
            <p className="text-gray-600 mb-4">
              As a user of this healthcare software system, you are required to acknowledge and agree to 
              the following HIPAA compliance requirements before accessing any Protected Health Information (PHI).
            </p>
          </div>

          {/* Key Points */}
          <div className="space-y-4 mb-6">
            <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
              <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-800">Access Control</h4>
                <p className="text-sm text-gray-600">
                  I will only access PHI that is necessary for my job duties and will not share my login credentials with anyone.
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
              <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-800">Minimum Necessary</h4>
                <p className="text-sm text-gray-600">
                  I will access, use, and disclose only the minimum amount of PHI necessary to perform my job functions.
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-800">Audit Trail</h4>
                <p className="text-sm text-gray-600">
                  I understand that all access to PHI is logged and audited for security and compliance purposes.
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-800">Reporting</h4>
                <p className="text-sm text-gray-600">
                  I will immediately report any suspected or actual breaches of PHI to the Privacy Officer or supervisor.
                </p>
              </div>
            </div>
          </div>

          {/* Legal Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-yellow-800 mb-2">Legal Notice</h4>
            <p className="text-sm text-yellow-700">
              Violations of HIPAA regulations can result in civil and criminal penalties, including fines up to 
              $1.5 million per violation category per year, and imprisonment for up to 10 years for certain offenses. 
              Unauthorized access or disclosure of PHI may also result in disciplinary action, up to and including termination.
            </p>
          </div>

          {/* Acknowledgment Checkbox */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={acknowledged}
                onCheckedChange={setAcknowledged}
                className="mt-1"
                data-testid="hipaa-acknowledge-checkbox"
              />
              <span className="text-sm text-gray-700">
                <strong>I acknowledge that I have read and understand the HIPAA compliance requirements.</strong> 
                I agree to comply with all applicable privacy and security regulations when accessing, using, 
                or disclosing Protected Health Information (PHI). I understand that my access to PHI will be 
                monitored and audited.
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <Button
            onClick={handleAcknowledge}
            disabled={!acknowledged || loading}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="hipaa-accept-btn"
          >
            {loading ? (
              'Processing...'
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                I Accept and Acknowledge
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HIPAAAcknowledgmentModal;
